import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Send, Loader2, MessageSquare, ClipboardEdit,
  CheckCircle, ChevronDown, ChevronRight, Save, UserPlus, Trash2
} from 'lucide-react';
import axios from 'axios';

import UseOfFundsScheduleCard from './UseOfFundsScheduleCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES_WITH_ADDITIONAL_OWNERS = ['sba_loan', 'term_loan', 'term_loan_10yr', 'term_loan_3_5yr', 'term_loc_hybrid'];

const OWNER_FIELDS = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'date_of_birth', label: 'Date of Birth' },
  { key: 'social_security_number', label: 'SSN' },
  { key: 'mobile_phone_number', label: 'Phone' },
  { key: 'email_address', label: 'Email' },
  { key: 'home_address', label: 'Home Address' },
  { key: 'percentage_of_ownership', label: 'Ownership %' },
];

// Sections for each loan type (mirrors AIApplicationFlow but without loan type labels)
const SECTION_MAP = {
  sba_loan: [
    { title: 'Business Information', fields: [
      { key: 'business_legal_name', label: 'Business Legal Name' },
      { key: 'doing_business_as', label: 'Doing Business As' },
      { key: 'legal_entity_type', label: 'Entity Type' },
      { key: 'business_start_date', label: 'Business Start Date' },
      { key: 'federal_tax_id', label: 'Federal Tax ID' },
      { key: 'business_phone_number', label: 'Business Phone' },
      { key: 'business_address', label: 'Business Address' },
      { key: 'naics_code_or_nature', label: 'Industry / NAICS Code' },
      { key: 'number_of_employees', label: 'Number of Employees' },
    ]},
    { title: 'Owner Information', fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'social_security_number', label: 'Social Security Number' },
      { key: 'mobile_phone_number', label: 'Mobile Phone' },
      { key: 'email_address', label: 'Email Address' },
      { key: 'home_address', label: 'Home Address' },
      { key: 'ownership_percentage', label: 'Ownership %' },
    ]},
    { title: 'Financial Details', fields: [
      { key: 'loan_requested_amount', label: 'Loan Amount Requested' },
      { key: 'total_monthly_sales', label: 'Monthly Sales' },
      { key: 'annual_income', label: 'Annual Income' },
      { key: 'has_existing_debt', label: 'Has Existing Debt' },
    ]},
    { title: 'Assets', fields: [
      { key: 'assets_cash', label: 'Cash' },
      { key: 'assets_home', label: 'Home Value' },
      { key: 'assets_automobile', label: 'Automobile' },
      { key: 'assets_investment_accounts', label: 'Investments' },
      { key: 'assets_other', label: 'Other Assets' },
    ]},
    { title: 'Liabilities', fields: [
      { key: 'liabilities_mortgage', label: 'Mortgage' },
      { key: 'liabilities_credit_cards', label: 'Credit Cards' },
      { key: 'liabilities_auto_loans', label: 'Auto Loans' },
      { key: 'liabilities_other', label: 'Other Liabilities' },
    ]},
    { title: 'Business Financials', fields: [
      { key: 'fiscal_year', label: 'Fiscal Year (e.g. 2024)' },
      { key: 'revenue', label: 'Revenue', fmt: 'currency' },
      { key: 'cost_of_goods_sold', label: 'Cost of Goods Sold', fmt: 'currency' },
      { key: 'payroll', label: 'Payroll', fmt: 'currency' },
      { key: 'rent', label: 'Rent', fmt: 'currency' },
      { key: 'other_operating_expenses', label: 'Other Operating Expenses', fmt: 'currency' },
    ]},
  ],
  term_loan_10yr: [
    { title: 'Owner Information', fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email_address', label: 'Email' },
      { key: 'phone_number', label: 'Phone' },
      { key: 'ownership_percentage', label: 'Ownership %' },
      { key: 'social_security_number', label: 'SSN' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'home_address', label: 'Home Address' },
    ]},
    { title: 'Business Information', fields: [
      { key: 'legal_corporate_name', label: 'Legal Corporate Name' },
      { key: 'doing_business_as', label: 'Doing Business As' },
      { key: 'business_address', label: 'Business Address' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'ZIP' },
      { key: 'entity_type', label: 'Entity Type' },
      { key: 'federal_tax_id', label: 'Federal Tax ID' },
      { key: 'industry_type', label: 'Industry' },
      { key: 'number_of_employees', label: 'Number of Employees' },
    ]},
    { title: 'Financial Details', fields: [
      { key: 'annual_revenue', label: 'Annual Revenue' },
      { key: 'monthly_revenue', label: 'Monthly Revenue' },
      { key: 'funding_amount_requested', label: 'Funding Requested' },
      { key: 'use_of_funds', label: 'Use of Funds' },
    ]},
  ],
  term_loan_3_5yr: [
    { title: 'Primary Borrower Contact Information', fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'primary_contact_ssn', label: 'Primary Contact SSN' },
    ]},
    { title: 'Primary Contact Address', fields: [
      { key: 'primary_street_address', label: 'Street Address' },
      { key: 'primary_street_address_2', label: 'Street Address 2' },
      { key: 'primary_city', label: 'City' },
      { key: 'primary_state', label: 'State' },
      { key: 'primary_zip', label: 'Zip' },
      { key: 'percentage_ownership', label: 'Percentage Ownership in Business' },
      { key: 'guaranty_type', label: 'Guaranty Type', fmt: 'select',
        options: ['Full Unsecured', 'Full Secured', 'Limited Unsecured', 'Limited Secured'] },
    ]},
    { title: 'Business Information', fields: [
      { key: 'legal_corporate_name', label: 'Legal/Corporate Name' },
      { key: 'company_name_dba', label: 'Company Name (DBA)' },
      { key: 'industry', label: 'Industry' },
      { key: 'office_space', label: 'Office Space', fmt: 'select', options: ['Rent', 'Own'] },
      { key: 'business_type', label: 'Business Type', fmt: 'select',
        options: ['Sole Proprietorship', 'C Corporation', 'S Corporation', 'Limited Liability Partnership', 'Partnership', 'Limited Liability Company', 'Other'] },
      { key: 'business_location_type', label: 'Business Location Type', fmt: 'select',
        options: ['Residential', 'Commercial'] },
      { key: 'state_of_incorporation', label: 'State of Incorporation' },
      { key: 'business_start_date', label: 'Business Start Date' },
      { key: 'type_of_business', label: 'Type of Business', fmt: 'select',
        options: ['Startup', 'Existing Business', 'Business Acquisition'] },
      { key: 'website', label: 'Website' },
    ]},
    { title: 'Physical Address', fields: [
      { key: 'physical_street_address', label: 'Street Address' },
      { key: 'physical_street_address_2', label: 'Street Address 2' },
      { key: 'physical_city', label: 'City' },
      { key: 'physical_state', label: 'State' },
      { key: 'physical_zip', label: 'Zip' },
    ]},
    { title: 'Mailing Address', fields: [
      { key: 'mailing_same_as_physical', label: 'Same as Physical Address', fmt: 'select', options: ['Yes', 'No'] },
      { key: 'mailing_street_address', label: 'Street Address' },
      { key: 'mailing_street_address_2', label: 'Street Address 2' },
      { key: 'mailing_city', label: 'City' },
      { key: 'mailing_state', label: 'State' },
      { key: 'mailing_zip', label: 'Zip' },
    ]},
    { title: 'Financial Information', fields: [
      { key: 'gross_annual_sales', label: 'Gross Annual Sales', fmt: 'currency' },
      { key: 'funding_amount_requested', label: 'Funding Amount Requested', fmt: 'currency' },
      { key: 'use_of_funds_description', label: 'Use of Funds Description', fmt: 'textarea' },
    ]},
  ],
  line_of_credit: [
    { title: 'Business Information', fields: [
      { key: 'business_name', label: 'Business Name' },
      { key: 'business_industry', label: 'Industry' },
      { key: 'years_in_business', label: 'Years in Business' },
      { key: 'business_zip_code', label: 'Business Zipcode' },
    ]},
    { title: 'Credit Details', fields: [
      { key: 'requested_line_of_credit_amount', label: 'Line of Credit Amount' },
      { key: 'purpose_of_credit', label: 'Purpose' },
    ]},
    { title: 'Owner Information', fields: [
      { key: 'owner_name', label: 'Owner Name' },
      { key: 'owner_address', label: 'Address' },
      { key: 'owner_ssn', label: 'SSN' },
      { key: 'owner_email', label: 'Email' },
      { key: 'owner_phone', label: 'Cell Phone' },
    ]},
  ],
  equipment_financing: [
    { title: 'Business Information', fields: [
      { key: 'business_name', label: 'Business Name' },
      { key: 'business_address', label: 'Address' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'ZIP' },
      { key: 'entity_type', label: 'Entity Type' },
      { key: 'federal_tax_id', label: 'Tax ID' },
      { key: 'years_in_business', label: 'Years in Business' },
      { key: 'industry', label: 'Industry' },
      { key: 'annual_revenue', label: 'Annual Revenue' },
    ]},
    { title: 'Owner Information', fields: [
      { key: 'owner_name', label: 'Full Name' },
      { key: 'owner_email', label: 'Email' },
      { key: 'owner_phone', label: 'Phone' },
      { key: 'owner_ssn', label: 'SSN' },
      { key: 'owner_dob', label: 'Date of Birth' },
      { key: 'owner_address', label: 'Address' },
      { key: 'ownership_percentage', label: 'Ownership %' },
    ]},
    { title: 'Equipment Details', fields: [
      { key: 'equipment_type', label: 'Equipment Type' },
      { key: 'equipment_description', label: 'Description' },
      { key: 'equipment_cost', label: 'Equipment Cost' },
      { key: 'new_or_used', label: 'New or Used' },
      { key: 'vendor_name', label: 'Vendor Name' },
    ]},
    { title: 'Financing Details', fields: [
      { key: 'amount_requested', label: 'Amount Requested' },
      { key: 'down_payment', label: 'Down Payment' },
      { key: 'desired_term_months', label: 'Term (Months)' },
      { key: 'use_of_equipment', label: 'Use of Equipment' },
    ]},
  ],
  term_loan: [
    { title: 'Owner Information', fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email_address', label: 'Email' },
      { key: 'phone_number', label: 'Phone' },
      { key: 'ownership_percentage', label: 'Ownership %' },
      { key: 'social_security_number', label: 'SSN' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'home_address', label: 'Home Address' },
    ]},
    { title: 'Business Information', fields: [
      { key: 'legal_corporate_name', label: 'Legal Corporate Name' },
      { key: 'doing_business_as', label: 'Doing Business As' },
      { key: 'business_address', label: 'Business Address' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'ZIP' },
      { key: 'entity_type', label: 'Entity Type' },
      { key: 'federal_tax_id', label: 'Federal Tax ID' },
      { key: 'industry_type', label: 'Industry' },
    ]},
    { title: 'Financial Details', fields: [
      { key: 'annual_revenue', label: 'Annual Revenue' },
      { key: 'monthly_revenue', label: 'Monthly Revenue' },
      { key: 'funding_amount_requested', label: 'Funding Requested' },
      { key: 'use_of_funds', label: 'Use of Funds' },
    ]},
  ],
  term_loc_hybrid: [
    { title: 'Business Information', fields: [
      { key: 'business_name', label: 'Business Name' },
      { key: 'doing_business_as', label: 'DBA' },
      { key: 'business_structure', label: 'Business Structure' },
      { key: 'average_annual_revenue', label: 'Avg Annual Revenue' },
      { key: 'date_established', label: 'Date Established' },
      { key: 'federal_tax_id', label: 'Tax ID' },
      { key: 'business_phone', label: 'Business Phone' },
      { key: 'naics_code', label: 'NAICS Code' },
    ]},
    { title: 'Business Address', fields: [
      { key: 'business_address', label: 'Address' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'ZIP' },
    ]},
    { title: 'Your Role', fields: [
      { key: 'role_at_business', label: 'Role' },
      { key: 'title_at_company', label: 'Title' },
      { key: 'ownership_percentage', label: 'Ownership %' },
      { key: 'years_with_business', label: 'Years with Business' },
      { key: 'years_industry_experience', label: 'Years Industry Experience' },
    ]},
    { title: 'Personal Information', fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email_address', label: 'Email' },
      { key: 'phone_number', label: 'Phone' },
      { key: 'ssn_itin', label: 'SSN/ITIN' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'place_of_birth', label: 'Place of Birth' },
    ]},
    { title: 'Identification', fields: [
      { key: 'id_type', label: 'ID Type' },
      { key: 'id_number', label: 'ID Number' },
      { key: 'id_state_issued', label: 'State Issued' },
      { key: 'id_expiration_date', label: 'Expiration Date' },
    ]},
    { title: 'Home Address', fields: [
      { key: 'owns_residence', label: 'Owns Residence' },
      { key: 'home_address', label: 'Address' },
      { key: 'home_city', label: 'City' },
      { key: 'home_state', label: 'State' },
      { key: 'home_zip', label: 'ZIP' },
    ]},
    { title: 'Loan Details', fields: [
      { key: 'use_of_proceeds', label: 'Use of Proceeds' },
      { key: 'total_loan_amount', label: 'Total Loan Amount' },
    ]},
  ],
  jumbo_term_loan: [
    { title: 'Business Information', fields: [
      { key: 'business_legal_name', label: 'Business Legal Name' },
      { key: 'business_dba_name', label: 'DBA Name' },
      { key: 'type_of_business_entity', label: 'Business Entity Type' },
      { key: 'industry_type', label: 'Industry Type' },
      { key: 'state_of_incorporation', label: 'State of Incorporation' },
      { key: 'business_street_address', label: 'Business Address' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'ZIP' },
      { key: 'amount_requested', label: 'Amount Requested' },
      { key: 'business_federal_tax_id', label: 'Federal Tax ID' },
      { key: 'business_start_date', label: 'Business Start Date' },
      { key: 'website', label: 'Website' },
    ]},
    { title: 'Owner Information', fields: [
      { key: 'owner_first_name', label: 'First Name' },
      { key: 'owner_last_name', label: 'Last Name' },
      { key: 'owner_dob', label: 'Date of Birth' },
      { key: 'owner_street_address', label: 'Home Address' },
      { key: 'owner_city', label: 'City' },
      { key: 'owner_state', label: 'State' },
      { key: 'owner_zip', label: 'ZIP' },
      { key: 'owner_ssn', label: 'SSN' },
    ]},
  ],
  merchant_processing: [
    { title: 'Contact Information', fields: [
      { key: 'contact_name', label: 'Contact Name' },
      { key: 'company_name', label: 'Company Name' },
      { key: 'industry_type', label: 'Industry Type' },
      { key: 'website_url', label: 'Website URL' },
      { key: 'years_in_business', label: 'Years in Business' },
    ]},
    { title: 'Point of Sale Details', fields: [
      { key: 'pos_software_or_terminals', label: 'POS Software or Terminals' },
      { key: 'pos_software_name', label: 'POS Software Name' },
      { key: 'number_of_terminals', label: 'Number of Terminals' },
      { key: 'terminal_brand_model', label: 'Terminal Brand and Model' },
    ]},
  ],
  transportation_factoring: [
    { title: 'Applicant Information', fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'mc_number', label: 'MC Number' },
      { key: 'email', label: 'Email' },
      { key: 'phone_number', label: 'Phone Number' },
    ]},
  ],
  real_estate_financing: [
    { title: 'Request for Financing', fields: [
      { key: 'financing_amount_requested', label: 'Financing Amount Requested', fmt: 'currency' },
      { key: 'down_payment_amount', label: 'Down Payment Amount', fmt: 'currency' },
      { key: 'detailed_use_of_funds', label: 'Detailed Use of Funds', fmt: 'textarea', hint: 'Please be as detailed as possible in explaining the scenario.' },
    ]},
    { title: 'Borrower Information', fields: [
      { key: 'borrower_first_name', label: 'First Name' },
      { key: 'borrower_last_name', label: 'Last Name' },
      { key: 'borrower_address', label: 'Street Address' },
      { key: 'borrower_city', label: 'City' },
      { key: 'borrower_state', label: 'State' },
      { key: 'borrower_zip', label: 'Zip' },
      { key: 'borrower_date_of_birth', label: 'Date of Birth' },
      { key: 'borrower_ssn', label: 'Social Security Number' },
      { key: 'borrower_email', label: 'Email' },
      { key: 'borrower_phone', label: 'Contact Number' },
      { key: 'borrower_citizenship_status', label: 'Citizenship Status', fmt: 'select',
        options: ['US Citizen', 'Legal Permanent Resident', 'Foreign National'] },
    ]},
    { title: 'Business Information', fields: [
      { key: 'legal_name_of_business', label: 'Legal Name of Business' },
      { key: 'business_in_good_standing', label: 'Business in Good Standing?', fmt: 'select', options: ['Yes', 'No'] },
      { key: 'date_established', label: 'Date Established' },
      { key: 'tax_id', label: 'Tax ID (EIN)' },
      { key: 'business_type', label: 'Business Type', fmt: 'select', options: ['Corp', 'LLC', 'Sole-Proprietor'] },
      { key: 'business_address', label: 'Business Address' },
      { key: 'business_city_state_zip', label: 'Business City, State, ZIP' },
      { key: 'mailing_address', label: 'Mailing Address' },
      { key: 'mailing_city_state_zip', label: 'Mailing City, State, ZIP' },
      { key: 'main_phone', label: 'Main Phone' },
      { key: 'fax', label: 'Fax' },
      { key: 'number_of_employees', label: '# of Employees' },
      { key: 'business_email', label: 'Business Email' },
      { key: 'type_of_business', label: 'Type of Business' },
    ]},
    { title: 'Property Information', fields: [
      { key: 'property_address', label: 'Property Address' },
      { key: 'property_type', label: 'Property Type' },
      { key: 'number_of_units', label: 'Number of Units' },
      { key: 'total_sqft', label: 'Total SQFT' },
      { key: 'purchase_price', label: 'Purchase Price', fmt: 'currency' },
      { key: 'property_under_contract', label: 'Property Under Contract?', fmt: 'select', options: ['Yes', 'No'] },
      { key: 'original_purchase_price', label: 'Original Purchase Price (if already owned)', fmt: 'currency' },
      { key: 'original_purchase_date', label: 'Original Purchase Date (if already owned)' },
      { key: 'existing_payoff_amount', label: 'Existing Payoff Amount (if already owned)', fmt: 'currency' },
      { key: 'as_is_value', label: 'As-Is Value', fmt: 'currency' },
      { key: 'rehab_budget', label: 'Rehab Budget', fmt: 'currency' },
      { key: 'estimated_after_repair_value', label: 'Estimated After Repair Value (ARV)', fmt: 'currency' },
    ]},
    { title: 'Financial Information', fields: [
      { key: 'liquidity_cash_to_close', label: 'Liquidity / Cash to Close', fmt: 'currency' },
      { key: 'exit_strategy', label: 'Exit Strategy', fmt: 'select', options: ['Sell', 'Refinance'] },
      { key: 'monthly_rental_income', label: 'Monthly Rental Income (if Refinance)', fmt: 'currency' },
      { key: 'annual_taxes', label: 'Annual Taxes', fmt: 'currency' },
      { key: 'annual_insurance', label: 'Annual Insurance', fmt: 'currency' },
      { key: 'estimated_middle_fico', label: 'Estimated Middle FICO Score' },
    ]},
    { title: 'Experience', fields: [
      { key: 'completed_rehab_projects_3yr', label: 'Completed Rehab Projects (Past 3 Years)' },
    ]},
    { title: 'Additional Information', fields: [
      { key: 'additional_notes', label: 'Additional Notes regarding the loan/borrower', fmt: 'textarea' },
    ]},
  ],
  foreclosure_bailout: [
    { title: 'Property Type', fields: [
      { key: 'property_type', label: 'Property Type', fmt: 'select',
        options: ['Single Family', '2-4 Units', 'Multifamily (5+ units)', 'Office', 'Retail', 'Mixed-Use', 'Church / Special Use'] },
    ]},
    { title: 'Property Location', fields: [
      { key: 'property_state', label: 'State' },
    ]},
    { title: 'Property / Loan Information', fields: [
      { key: 'estimated_property_value', label: 'Estimated Property Value', fmt: 'currency' },
      { key: 'total_mortgage_balances', label: 'Total Amount of Mortgage Balances', fmt: 'currency', hint: 'Loan Amount will auto-populate to match this value.' },
      { key: 'loan_amount', label: 'Loan Amount (auto)', fmt: 'currency', hint: 'Automatically populated from Total Mortgage Balances.' },
    ]},
    { title: 'Foreclosure Information', fields: [
      { key: 'foreclosure_sale_scheduled', label: 'Is a foreclosure sale or auction date scheduled?', fmt: 'select', options: ['Yes', 'No'] },
      { key: 'foreclosure_sale_date', label: 'Foreclosure sale / auction date (if scheduled)' },
    ]},
    { title: 'Borrower Information', fields: [
      { key: 'business_name', label: 'Business Name' },
      { key: 'borrower_first_name', label: 'Borrower First Name' },
      { key: 'borrower_last_name', label: 'Borrower Last Name' },
      { key: 'borrower_email', label: 'Borrower Email' },
      { key: 'borrower_phone', label: 'Borrower Phone' },
    ]},
    { title: 'Vesting', fields: [
      { key: 'title_held_in_entity', label: 'Is the title held in an entity?', fmt: 'select', options: ['Yes', 'No'] },
      { key: 'entity_name', label: 'Entity Name', showIf: (d) => d?.title_held_in_entity === 'Yes' },
      { key: 'entity_ein', label: 'Entity EIN', showIf: (d) => d?.title_held_in_entity === 'Yes' },
    ]},
    { title: 'Loan Structure', fields: [
      { key: 'payment_structure', label: 'Payment Structure', hint: '2-year term, amortized over 40 years (fixed).' },
      { key: 'broker_fee_percent', label: 'Broker Fee (%)', hint: 'Maximum 2%.' },
    ]},
  ],
  family_office: [
    { title: 'Sub-Programs (choose one or more)', fields: [
      { key: 'selected_subtypes', label: 'Programs of Interest', fmt: 'multiselect',
        options: ['Term Loans', 'Lines of Credit', 'Non-Collateralized Loans', 'Asset-Based Lending', 'Equipment Financing'],
        optionValues: ['term_loans', 'lines_of_credit', 'non_collateralized_loans', 'asset_based_lending', 'equipment_financing'] },
    ]},
    { title: 'Business Information', fields: [
      { key: 'legal_business_name', label: 'Legal Business Name' },
      { key: 'dba', label: 'Doing Business As (DBA)' },
      { key: 'business_phone', label: 'Business Phone' },
      { key: 'business_email', label: 'Business Email' },
      { key: 'business_street_address', label: 'Business Street Address' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'Zip' },
      { key: 'business_start_date', label: 'Business Start Date' },
      { key: 'business_type_services', label: 'Business Type / Services Provided', fmt: 'textarea' },
      { key: 'legal_structure', label: 'Legal Structure', fmt: 'select',
        options: ['Sole Proprietorship', 'Partnership', 'Corporation', 'LLC', 'Other'] },
      { key: 'landlord_name', label: 'Landlord Name' },
      { key: 'landlord_phone', label: 'Landlord Phone' },
    ]},
    { title: 'Financial Information', fields: [
      { key: 'federal_tax_id', label: 'Federal Tax ID / EIN (9 digits)' },
      { key: 'state_of_incorporation', label: 'State of Incorporation' },
      { key: 'requested_funding_amount', label: 'Requested Funding Amount', fmt: 'currency' },
      { key: 'purpose_of_funds', label: 'Purpose of Funds', fmt: 'textarea' },
    ]},
    { title: 'Advances & Debts', fields: [
      { key: 'has_merchant_advances', label: 'Do you currently have any merchant cash advances or business loans outstanding?', fmt: 'select', options: ['Yes', 'No'] },
      { key: 'has_other_business_debts', label: 'Do you have any other outstanding business debts?', fmt: 'select', options: ['Yes', 'No'] },
    ]},
    { title: 'Outstanding Debts / Advances', fmt: 'repeater', repeaterKey: 'debts', addButtonLabel: 'Add Another Debt', minRows: 0, fields: [
      { key: 'funding_company', label: 'Funding Company' },
      { key: 'amount_funded', label: 'Amount Funded', fmt: 'currency' },
      { key: 'date_funded', label: 'Date Funded' },
      { key: 'payment_amount', label: 'Daily/Weekly Payment', fmt: 'currency' },
      { key: 'current_balance', label: 'Current Balance', fmt: 'currency' },
      { key: 'weeks_remaining', label: 'Payback Weeks Remaining' },
    ]},
    { title: 'Ownership — Primary Owner', fields: [
      { key: 'owner1_first_name', label: 'First Name' },
      { key: 'owner1_last_name', label: 'Last Name' },
      { key: 'owner1_email', label: 'Email' },
      { key: 'owner1_phone', label: 'Phone' },
      { key: 'owner1_date_of_birth', label: 'Date of Birth' },
      { key: 'owner1_ssn', label: 'Social Security Number' },
      { key: 'owner1_ownership_percent', label: 'Ownership %' },
      { key: 'owner1_home_street_address', label: 'Home Street Address' },
      { key: 'owner1_home_city', label: 'City' },
      { key: 'owner1_home_state', label: 'State' },
      { key: 'owner1_home_zip', label: 'Zip' },
    ]},
    { title: 'Additional Owners', fmt: 'repeater', repeaterKey: 'additional_owners', addButtonLabel: 'Add Another Owner', minRows: 0, fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'ssn', label: 'Social Security Number' },
      { key: 'ownership_percent', label: 'Ownership %' },
      { key: 'home_street_address', label: 'Home Street Address' },
      { key: 'home_city', label: 'City' },
      { key: 'home_state', label: 'State' },
      { key: 'home_zip', label: 'Zip' },
    ]},
  ],
  debt_consulting: [
    { title: 'Business Information', fields: [
      { key: 'legal_business_name', label: 'Legal Business Name' },
      { key: 'dba', label: 'Doing Business As (DBA)' },
      { key: 'business_phone', label: 'Business Phone' },
      { key: 'business_email', label: 'Business Email' },
      { key: 'business_street_address', label: 'Business Street Address' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'Zip' },
      { key: 'business_start_date', label: 'Business Start Date' },
      { key: 'business_type_services', label: 'Business Type / Services Provided', fmt: 'textarea' },
      { key: 'legal_structure', label: 'Legal Structure', fmt: 'select',
        options: ['Sole Proprietorship', 'Partnership', 'Corporation', 'LLC', 'Other'] },
    ]},
    { title: 'Financial Information', fields: [
      { key: 'federal_tax_id', label: 'Federal Tax ID / EIN' },
      { key: 'state_of_incorporation', label: 'State of Incorporation' },
      { key: 'requested_funding_amount', label: 'Requested Consolidation Amount', fmt: 'currency' },
      { key: 'purpose_of_funds', label: 'Purpose of Consolidation', fmt: 'textarea' },
    ]},
    { title: 'Outstanding Debts', fmt: 'repeater', repeaterKey: 'debts', addButtonLabel: 'Add Another Debt', minRows: 0, fields: [
      { key: 'funding_company', label: 'Funding Company' },
      { key: 'amount_funded', label: 'Amount Funded', fmt: 'currency' },
      { key: 'date_funded', label: 'Date Funded' },
      { key: 'payment_amount', label: 'Daily/Weekly Payment', fmt: 'currency' },
      { key: 'current_balance', label: 'Current Balance', fmt: 'currency' },
      { key: 'weeks_remaining', label: 'Payback Weeks Remaining' },
    ]},
    { title: 'Ownership — Primary Owner', fields: [
      { key: 'owner1_first_name', label: 'First Name' },
      { key: 'owner1_last_name', label: 'Last Name' },
      { key: 'owner1_email', label: 'Email' },
      { key: 'owner1_phone', label: 'Phone' },
      { key: 'owner1_date_of_birth', label: 'Date of Birth' },
      { key: 'owner1_ownership_percent', label: 'Ownership %' },
    ]},
  ],
};

const BorrowerCompletionFlow = ({ token, initialData, loanTypes, onComplete, requestedFields = [], customInfoRequests = [], applicationStatus, allowCompletion = false, productType = '', useOfFundsSchedule = [], amountRequested = 0 }) => {
  const [mode, setMode] = useState('review'); // Always start on fields view
  const [formData, setFormData] = useState(initialData || {});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [customResponses, setCustomResponses] = useState({});
  const chatEndRef = useRef(null);

  const hasRequestedInfo = requestedFields.length > 0 || customInfoRequests.length > 0;

  const hasStarted = useRef(false);

  // Only start AI chat when user switches to chat mode for the first time
  useEffect(() => {
    if (mode === 'chat' && !hasStarted.current && !hasRequestedInfo) {
      hasStarted.current = true;
      handleSendMessage('Hi, I\'d like to complete my application.');
    }
  }, [mode]); // eslint-disable-line

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Compute filled/total fields
  const fieldStats = React.useMemo(() => {
    let filled = 0, total = 0;
    for (const lt of loanTypes) {
      const dataKey = `${lt}_data`;
      const sections = SECTION_MAP[lt] || [];
      const data = formData[dataKey] || {};
      for (const sec of sections) {
        // Repeater sections — count 1 per row (any field filled = row filled)
        if (sec.fmt === 'repeater') {
          const rows = Array.isArray(data[sec.repeaterKey]) ? data[sec.repeaterKey] : [];
          for (const row of rows) {
            total++;
            if (row && sec.fields.some(f => row[f.key] !== undefined && row[f.key] !== null && row[f.key] !== '')) filled++;
          }
          continue;
        }
        for (const f of (sec.fields || [])) {
          if (typeof f.showIf === 'function' && !f.showIf(data)) continue;
          total++;
          const v = data[f.key];
          if (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== '') filled++;
        }
      }
    }
    return { filled, total };
  }, [formData, loanTypes]);

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setSending(true);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/borrower-chat/${token}`, {
        message: text,
        current_data: formData,
        conversation: newMessages.slice(-12),
      });

      const { message, field_updates, progress: newProgress } = res.data;
      setMessages(prev => [...prev, { role: 'assistant', content: message }]);

      if (newProgress) setProgress(newProgress);

      // Apply field updates
      if (field_updates && Object.keys(field_updates).length > 0) {
        setFormData(prev => {
          const updated = { ...prev };
          for (const [dataKey, fields] of Object.entries(field_updates)) {
            if (fields && typeof fields === 'object') {
              updated[dataKey] = { ...(updated[dataKey] || {}), ...fields };
            }
          }
          return updated;
        });
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I\'m having trouble right now. Please try again or use the Review & Edit form to enter your information manually.' }]);
    } finally {
      setSending(false);
    }
  };

  const handleFieldChange = (dataKey, fieldKey, value) => {
    setFormData(prev => {
      const nextData = { ...(prev[dataKey] || {}), [fieldKey]: value };
      // Foreclosure Bailout: loan_amount is always mirrored from
      // total_mortgage_balances per product spec. Keep it in sync as the
      // user types (unless they've manually overridden loan_amount already).
      if (dataKey === 'foreclosure_bailout_data' && fieldKey === 'total_mortgage_balances') {
        nextData.loan_amount = value;
      }
      return { ...prev, [dataKey]: nextData };
    });
  };

  const handleSaveFields = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/public/borrower-update/${token}`, { form_data: formData });
    } catch (err) { /* silent */ }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    // When the broker shared an editable link on a SUBMITTED app, the
    // "Submit" action means "save my changes" — there's no status
    // transition to perform. Route to the save-only endpoint.
    const isCompletionUpdate = allowCompletion && applicationStatus && applicationStatus !== 'incomplete';
    const confirmText = isCompletionUpdate
      ? 'Save the updates you made to this application?'
      : hasRequestedInfo
        ? 'Submit your responses to the requested information?'
        : 'Are you sure you want to submit your application? You will not be able to edit it after submission.';
    if (!window.confirm(confirmText)) return;
    setSubmitting(true);
    try {
      if (isCompletionUpdate) {
        await axios.put(`${BACKEND_URL}/api/public/borrower-update/${token}`, { form_data: formData });
        alert('Your changes have been saved. Thank you!');
      } else if (hasRequestedInfo) {
        await axios.post(`${BACKEND_URL}/api/public/borrower-submit-info/${token}`, { form_data: formData, custom_responses: customResponses });
      } else {
        await axios.post(`${BACKEND_URL}/api/public/borrower-submit/${token}`, { form_data: formData });
      }
      if (onComplete) onComplete();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Build the filtered field list for requested info mode
  const requestedFieldDefs = React.useMemo(() => {
    if (!hasRequestedInfo || requestedFields.length === 0) return [];
    const defs = [];
    for (const lt of loanTypes) {
      const dataKey = `${lt}_data`;
      const sections = SECTION_MAP[lt] || [];
      for (const sec of sections) {
        for (const f of sec.fields) {
          if (requestedFields.includes(f.key) && !defs.find(d => d.key === f.key)) {
            defs.push({ ...f, dataKey });
          }
        }
      }
    }
    return defs;
  }, [requestedFields, loanTypes, hasRequestedInfo]);

  // If in requested-info mode, show a focused form instead of the full flow
  if (hasRequestedInfo) {
    return (
      <div className="space-y-4" data-testid="borrower-requested-info-flow">
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
          <h3 className="font-semibold text-purple-800 text-sm mb-1">Additional Information Requested</h3>
          <p className="text-xs text-purple-600">Please fill in the information below and submit when complete.</p>
        </div>

        {/* Requested application fields */}
        {requestedFieldDefs.length > 0 && (
          <Card data-testid="requested-fields-form">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-medium">Application Fields</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {requestedFieldDefs.map(f => (
                  <div key={f.key}>
                    <Label className="text-xs text-slate-600">{f.label}</Label>
                    <Input
                      value={(formData[f.dataKey] || {})[f.key] || ''}
                      onChange={(e) => handleFieldChange(f.dataKey, f.key, e.target.value)}
                      className="mt-1 h-8 text-sm"
                      placeholder={`Enter ${f.label.toLowerCase()}`}
                      data-testid={`requested-field-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Custom info requests */}
        {customInfoRequests.length > 0 && (
          <Card data-testid="custom-info-requests-form">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-medium">Additional Requests</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0 space-y-3">
              {customInfoRequests.map((req, i) => (
                <div key={i}>
                  <Label className="text-xs text-slate-600">{req}</Label>
                  <textarea
                    value={customResponses[i] || ''}
                    onChange={(e) => setCustomResponses(prev => ({ ...prev, [i]: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-purple-200 focus:border-purple-400 outline-none resize-y min-h-[60px]"
                    placeholder="Enter your response..."
                    data-testid={`custom-response-${i}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Submit */}
        <div className="flex justify-center pt-2">
          <Button onClick={handleSubmit} disabled={submitting} className="bg-purple-600 hover:bg-purple-700 px-8" data-testid="submit-requested-info-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Submit Information
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="borrower-completion-flow">
      {/* Mode Switcher */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={mode === 'review' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('review')}
            data-testid="borrower-review-tab"
          >
            <ClipboardEdit className="h-4 w-4 mr-1" /> Review & Edit
          </Button>
          <Button
            variant={mode === 'chat' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('chat')}
            data-testid="borrower-chat-tab"
          >
            <MessageSquare className="h-4 w-4 mr-1" /> AI Assistant
          </Button>
        </div>
        <Badge variant="outline" className="text-xs">
          {fieldStats.filled}/{fieldStats.total} fields
        </Badge>
      </div>

      {/* Tip */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 text-sm text-blue-700" data-testid="borrower-tip">
        Fill in the fields below, or click <button onClick={() => setMode('chat')} className="font-semibold underline underline-offset-2 hover:text-blue-900">AI Assistant</button> to get help completing your application.
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2">
        <div
          className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${Math.max(progress, Math.round((fieldStats.filled / Math.max(fieldStats.total, 1)) * 100))}%` }}
        />
      </div>

      {mode === 'chat' ? (
        /* AI Chat Mode */
        <Card>
          <CardContent className="p-0">
            <div className="h-[400px] overflow-y-auto p-4 space-y-3" data-testid="borrower-chat-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm whitespace-pre-line ${
                    msg.role === 'user'
                      ? 'bg-[#081120] text-white'
                      : 'bg-slate-100 text-slate-800'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-lg px-4 py-2.5">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); handleSendMessage(input); }}
              className="flex items-center gap-2 border-t p-3"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your response..."
                disabled={sending}
                className="flex-1"
                data-testid="borrower-chat-input"
              />
              <Button type="submit" size="sm" disabled={sending || !input.trim()} data-testid="borrower-chat-send">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
      ) : (
        /* Review & Edit Mode */
        <div className="space-y-3" data-testid="borrower-review-form">
          {loanTypes.map(lt => {
            const dataKey = `${lt}_data`;
            const sections = SECTION_MAP[lt] || [];
            const data = formData[dataKey] || {};
            return sections.map((section, si) => {
            return (() => {
              // Repeater section — renders a dynamic list of sub-forms with
              // Add/Remove controls.
              if (section.fmt === 'repeater') {
                const sectionKey = `${lt}_${si}`;
                const isExpanded = expandedSections[sectionKey] !== false;
                const rows = Array.isArray(data[section.repeaterKey]) ? data[section.repeaterKey] : [];
                const filledCount = rows.filter(r => r && section.fields.some(f => r[f.key] !== undefined && r[f.key] !== null && r[f.key] !== '')).length;

                const addRow = () => {
                  const newRows = [...rows, {}];
                  handleFieldChange(dataKey, section.repeaterKey, newRows);
                };
                const removeRow = (idx) => {
                  const newRows = rows.filter((_, i) => i !== idx);
                  handleFieldChange(dataKey, section.repeaterKey, newRows);
                };
                const updateRowField = (idx, fieldKey, value) => {
                  const newRows = rows.map((r, i) => i === idx ? { ...(r || {}), [fieldKey]: value } : r);
                  handleFieldChange(dataKey, section.repeaterKey, newRows);
                };

                return (
                  <Card key={sectionKey} className={filledCount > 0 ? 'border-amber-200' : ''}>
                    <CardHeader
                      className="cursor-pointer py-3 px-4"
                      onClick={() => toggleSection(sectionKey)}
                    >
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          {section.title}
                        </CardTitle>
                        <Badge variant="outline" className="text-xs">
                          {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
                        </Badge>
                      </div>
                    </CardHeader>
                    {isExpanded && (
                      <CardContent className="pt-0 px-4 pb-4 space-y-3">
                        {rows.length === 0 && (
                          <p className="text-xs text-slate-500 italic">No entries yet. Click "{section.addButtonLabel || 'Add Entry'}" to add one.</p>
                        )}
                        {rows.map((row, ridx) => (
                          <div key={ridx} className="border rounded-md p-3 bg-slate-50" data-testid={`repeater-${section.repeaterKey}-row-${ridx}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-slate-700">Entry {ridx + 1}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 h-7 px-2 text-xs"
                                onClick={() => removeRow(ridx)}
                                data-testid={`repeater-${section.repeaterKey}-remove-${ridx}`}
                              >
                                Remove
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {section.fields.map(f => (
                                <div key={f.key} className={f.fmt === 'textarea' ? 'md:col-span-2' : ''}>
                                  <Label className="text-xs text-slate-600">{f.label}</Label>
                                  <Input
                                    type={f.fmt === 'currency' ? 'number' : 'text'}
                                    value={(row || {})[f.key] || ''}
                                    onChange={(e) => updateRowField(ridx, f.key, e.target.value)}
                                    className="mt-1 h-8 text-sm"
                                    placeholder={`Enter ${f.label.toLowerCase()}`}
                                    data-testid={`repeater-${section.repeaterKey}-${ridx}-${f.key}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addRow}
                          className="w-full border-dashed"
                          data-testid={`repeater-add-${section.repeaterKey}`}
                        >
                          + {section.addButtonLabel || 'Add Entry'}
                        </Button>
                      </CardContent>
                    )}
                  </Card>
                );
              }

              // Respect any `showIf(data)` predicates so conditional fields
              // (e.g. Foreclosure Bailout: Entity Name/EIN only when title is
              // held in an entity) don't count toward the section total or
              // render as empty rows.
              const visibleFields = section.fields.filter(
                (f) => typeof f.showIf !== 'function' || f.showIf(data)
              );
              if (visibleFields.length === 0) return null;
              const sectionKey = `${lt}_${si}`;
              const isExpanded = expandedSections[sectionKey] !== false;
              const filledCount = visibleFields.filter(f => {
                const v = data[f.key];
                return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== '';
              }).length;
              const allFilled = filledCount === visibleFields.length;
              return (
                <Card key={sectionKey} className={allFilled ? 'border-emerald-200' : filledCount > 0 ? 'border-amber-200' : ''}>
                  <CardHeader
                    className="cursor-pointer py-3 px-4"
                    onClick={() => toggleSection(sectionKey)}
                  >
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {section.title}
                      </CardTitle>
                      <Badge variant="outline" className={`text-xs ${allFilled ? 'bg-emerald-50 text-emerald-700' : ''}`}>
                        {filledCount}/{visibleFields.length}
                      </Badge>
                    </div>
                  </CardHeader>
                  {isExpanded && (
                    <CardContent className="pt-0 px-4 pb-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {visibleFields.map(f => (
                          <div key={f.key} className={(f.fmt === 'textarea' || f.fmt === 'multiselect') ? 'md:col-span-2' : ''}>
                            <Label className="text-xs text-slate-600">{f.label}</Label>
                            {f.fmt === 'select' ? (
                              <select
                                value={data[f.key] ?? ''}
                                onChange={(e) => handleFieldChange(dataKey, f.key, e.target.value)}
                                className="mt-1 h-8 text-sm w-full rounded-md border border-input bg-background px-2"
                                data-testid={`field-${f.key}`}
                              >
                                <option value="">Select...</option>
                                {(f.options || []).map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : f.fmt === 'multiselect' ? (
                              <div className="mt-1 flex flex-wrap gap-3 px-3 py-2 border rounded-md bg-white" data-testid={`field-${f.key}`}>
                                {(f.options || []).map((opt, oi) => {
                                  const arr = Array.isArray(data[f.key]) ? data[f.key] : [];
                                  const storedValue = (f.optionValues && f.optionValues[oi]) ?? opt;
                                  const checked = arr.includes(storedValue);
                                  return (
                                    <label key={opt} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          const next = e.target.checked ? [...arr, storedValue] : arr.filter(v => v !== storedValue);
                                          handleFieldChange(dataKey, f.key, next);
                                        }}
                                      />
                                      {opt}
                                    </label>
                                  );
                                })}
                              </div>
                            ) : f.fmt === 'textarea' ? (
                              <textarea
                                rows={3}
                                value={data[f.key] || ''}
                                onChange={(e) => handleFieldChange(dataKey, f.key, e.target.value)}
                                className="mt-1 text-sm w-full rounded-md border border-input bg-background px-2 py-1.5"
                                placeholder={f.placeholder || f.label}
                                data-testid={`field-${f.key}`}
                              />
                            ) : (
                              <Input
                                type={f.fmt === 'currency' ? 'number' : 'text'}
                                value={data[f.key] || ''}
                                onChange={(e) => handleFieldChange(dataKey, f.key, e.target.value)}
                                className="mt-1 h-8 text-sm"
                                placeholder={`Enter ${f.label.toLowerCase()}`}
                                data-testid={`field-${f.key}`}
                              />
                            )}
                            {f.hint && (
                              <p className="mt-1 text-xs text-slate-500 italic" data-testid={`field-${f.key}-hint`}>{f.hint}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })();
          });
          })}

          {/* Additional Owners Section */}
          {loanTypes.filter(lt => TYPES_WITH_ADDITIONAL_OWNERS.includes(lt)).map(lt => {
            const dataKey = `${lt}_data`;
            const data = formData[dataKey] || {};
            const owners = data.additional_owners || [];

            const addOwner = () => {
              const newOwner = {};
              OWNER_FIELDS.forEach(f => { newOwner[f.key] = ''; });
              handleFieldChange(dataKey, 'additional_owners', [...owners, newOwner]);
            };

            const removeOwner = (idx) => {
              handleFieldChange(dataKey, 'additional_owners', owners.filter((_, i) => i !== idx));
            };

            const updateOwner = (idx, field, value) => {
              const updated = owners.map((o, i) => i === idx ? { ...o, [field]: value } : o);
              handleFieldChange(dataKey, 'additional_owners', updated);
            };

            return (
              <Card key={`${lt}_owners`} data-testid="additional-owners-section">
                <CardHeader className="cursor-pointer py-3 px-4" onClick={() => toggleSection(`${lt}_owners`)}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      {expandedSections[`${lt}_owners`] !== false ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Additional Owners / Guarantors
                    </CardTitle>
                    <Badge variant="outline" className="text-xs">{owners.length} owner{owners.length !== 1 ? 's' : ''}</Badge>
                  </div>
                </CardHeader>
                {expandedSections[`${lt}_owners`] !== false && (
                  <CardContent className="pt-0 px-4 pb-4 space-y-3">
                    {owners.map((owner, idx) => (
                      <div key={idx} className="border border-slate-200 rounded-lg p-3 relative" data-testid={`borrower-owner-${idx}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-slate-500">Owner {idx + 1}</span>
                          <button onClick={() => removeOwner(idx)} className="text-red-400 hover:text-red-600 p-0.5" data-testid={`remove-owner-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {OWNER_FIELDS.map(f => (
                            <div key={f.key}>
                              <Label className="text-xs text-slate-600">{f.label}</Label>
                              <Input
                                value={owner[f.key] || ''}
                                onChange={(e) => updateOwner(idx, f.key, e.target.value)}
                                className="mt-0.5 h-8 text-sm"
                                placeholder={f.label}
                                data-testid={`owner-${idx}-${f.key}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addOwner}
                      className="w-full text-xs"
                      data-testid="borrower-add-owner-btn"
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Add Additional Owner
                    </Button>
                  </CardContent>
                )}
              </Card>
            );
          })}

          {/* Use of Funds Schedule — every product with a detailed_use_of_funds field */}
          {productType && productType !== 'merchant_processing' && (
            <Card data-testid="borrower-use-of-funds-section">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-medium">Use of Funds Schedule</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <UseOfFundsScheduleCard
                  token={token}
                  items={useOfFundsSchedule}
                  amountRequested={amountRequested}
                />
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={handleSaveFields} disabled={saving} data-testid="borrower-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Progress
            </Button>
          </div>
        </div>
      )}

      {/* Submit button */}
      <div className="flex justify-center pt-2">
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-emerald-600 hover:bg-emerald-700 px-8"
          data-testid="borrower-submit-btn"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
          {allowCompletion && applicationStatus && applicationStatus !== 'incomplete' ? 'Save Updates' : 'Submit Application'}
        </Button>
      </div>
    </div>
  );
};

export default BorrowerCompletionFlow;
